import React, { useState, useEffect } from 'react';
import axios from '../api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  Autocomplete,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useToast } from './ToastProvider';
import PageLoader from './ui/PageLoader';

const labelSx = {
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'text.secondary',
  mb: 0.5,
  display: 'block',
};

const sectionTitleSx = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'text.primary',
  mb: 1.5,
  display: 'block',
};

const inputFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.25,
    fontSize: '0.875rem',
    '& fieldset': { borderColor: 'divider' },
  },
};

const autocompleteSx = {
  width: '100%',
  minWidth: 0,
  '& .MuiAutocomplete-inputRoot': {
    width: '100%',
  },
};

const renderSearchInput = (placeholder) => (params) => (
  <TextField
    {...params}
    placeholder={placeholder}
    required
    size="small"
    fullWidth
    sx={{ ...inputFieldSx, width: '100%' }}
  />
);

const renderSearchOption = (primary, secondary) => (props, option) => {
  const { key, ...optionProps } = props;
  return (
    <li key={key} {...optionProps}>
      <Box>
        <Typography sx={{ fontSize: '0.875rem' }}>{primary(option)}</Typography>
        {secondary(option) && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{secondary(option)}</Typography>
        )}
      </Box>
    </li>
  );
};

const filterProducts = (options, { inputValue }) => {
  const search = inputValue.toLowerCase().trim();
  if (!search) return options;
  return options.filter((opt) => {
    const name = (opt.name || '').toLowerCase();
    const code = (opt.product_code || '').toLowerCase();
    const category = (opt.category || '').toLowerCase();
    return name.includes(search) || code.includes(search) || category.includes(search);
  });
};

const getProductLabel = (product) => product.name || '';

const BookingEditModal = ({ isOpen, booking, onClose, onSubmit }) => {
  const [formData, setFormData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [missingData, setMissingData] = useState(false);
  const showToast = useToast();

  useEffect(() => {
    if (!isOpen) {
      setFormData(null);
      setLoadingData(true);
      setMissingData(false);
      setErrorMsg('');
    }
  }, [isOpen]);

  useEffect(() => {
    let isMounted = true;
    const fetchAll = async () => {
      setLoadingData(true);
      try {
        const prodRes = await axios.get('/api/products', { params: { status: 'active' } });
        if (isMounted) {
          setProducts(prodRes.data);
          if (booking) {
            const customer_id = booking.customer_id ? String(booking.customer_id) : '';
            let productsArr = [{ product_id: '', quantity: '' }];
            let missing = false;
            if (booking.products && Array.isArray(booking.products) && booking.products.length > 0) {
              productsArr = booking.products.map(p => {
                let product_id = '';
                if (p.product_id) {
                  product_id = String(p.product_id);
                } else if (p.product_name) {
                  const found = prodRes.data.find(prod => prod.name === p.product_name);
                  if (found) product_id = String(found.id);
                }
                if (!product_id) missing = true;
                return {
                  product_id,
                  quantity: p.quantity?.toString() || ''
                };
              });
            } else {
              missing = true;
            }
            setFormData({
              customer_id,
              dispatch_date: booking.dispatch_date || '',
              return_date: booking.return_date || '',
              products: productsArr,
              discount: booking.discount != null ? String(booking.discount) : '',
              advance_payment: booking.advance_payment != null ? String(booking.advance_payment) : '',
              transportation_charges:
                booking.transportation_charges != null ? String(booking.transportation_charges) : '',
            });
            setMissingData(missing || !customer_id);
          }
        }
      } finally {
        if (isMounted) setLoadingData(false);
      }
    };
    if (isOpen && booking) {
      fetchAll();
    }
    return () => { isMounted = false; };
  }, [isOpen, booking]);

  const handleGeneralChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductChange = (index, field, value) => {
    const newProducts = [...formData.products];
    newProducts[index][field] = value;
    setFormData(prev => ({ ...prev, products: newProducts }));
  };

  const addProductRow = () => {
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, { product_id: '', quantity: '' }]
    }));
  };

  const removeProductRow = index => {
    if (formData.products.length === 1) return;
    const newProducts = formData.products.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, products: newProducts }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    try {
      const payload = {
        customer_id: parseInt(formData.customer_id),
        dispatch_date: formData.dispatch_date,
        return_date: formData.return_date || null,
        products: formData.products.map(p => ({
          product_id: parseInt(p.product_id),
          quantity: parseInt(p.quantity)
        }))
      };
      if (formData.discount !== '' && formData.discount != null) {
        const d = parseFloat(formData.discount);
        if (!Number.isNaN(d)) payload.discount = d;
      }
      if (formData.advance_payment !== '' && formData.advance_payment != null) {
        const a = parseFloat(formData.advance_payment);
        if (!Number.isNaN(a)) payload.advance_payment = a;
      }
      if (formData.transportation_charges !== '' && formData.transportation_charges != null) {
        const t = parseFloat(formData.transportation_charges);
        if (!Number.isNaN(t)) payload.transportation_charges = t;
      }
      await onSubmit(payload);
    } catch (err) {
      let msg = 'Update failed.';
      if (err.response?.data?.error) msg = err.response.data.error;
      else if (err.message) msg = err.message;
      setErrorMsg(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const modalPaperSx = {
    borderRadius: 2,
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
    bgcolor: 'background.paper',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
  };

  if (!booking || !formData || loadingData) return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: modalPaperSx }}>
      <DialogTitle sx={{ fontSize: '1.125rem', fontWeight: 600, px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        Edit Booking
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
        <PageLoader variant="inline" message="Loading booking..." />
      </DialogContent>
    </Dialog>
  );

  const totalAmount = booking?.total_amount ?? 0;
  const discountNum = parseFloat(formData.discount) || 0;
  const transportNum = parseFloat(formData.transportation_charges) || 0;
  const finalPrice = Math.max(0, totalAmount + transportNum - discountNum);
  const advanceNum = parseFloat(formData.advance_payment) || 0;
  const customerDisplayName = booking?.company_name || booking?.customer || '—';

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
      sx={{ '& .MuiDialog-paper': { mx: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
    >
      <DialogTitle
        sx={{
          fontSize: { xs: '1rem', sm: '1.125rem' },
          fontWeight: 600,
          color: 'text.primary',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>Edit Booking</span>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'auto', flex: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
        {missingData && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Some booking details could not be loaded. Please review and fill in missing fields before saving.
          </Alert>
        )}
        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
        <Box component="form" onSubmit={handleSubmit} id="edit-booking-form">
          {/* Customer */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelSx}>Customer</Typography>
            <TextField
              value={customerDisplayName}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              sx={{
                ...inputFieldSx,
                '& .MuiOutlinedInput-root': {
                  ...inputFieldSx['& .MuiOutlinedInput-root'],
                  bgcolor: 'grey.50',
                  '& fieldset': { borderColor: 'divider' },
                },
              }}
            />
          </Box>

          {/* Dispatch & Return Date */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Dispatch Date *</Typography>
              <TextField
                type="date"
                name="dispatch_date"
                value={formData.dispatch_date}
                onChange={handleGeneralChange}
                required
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Return Date</Typography>
              <TextField
                type="date"
                name="return_date"
                value={formData.return_date}
                onChange={handleGeneralChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={inputFieldSx}
              />
            </Grid>
          </Grid>

          {/* Products */}
          <Typography sx={sectionTitleSx}>Products</Typography>
          {formData.products.map((p, index) => {
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  mb: 1.5,
                  width: '100%',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={labelSx}>Product</Typography>
                  <Autocomplete
                    options={products}
                    value={products.find((pr) => String(pr.id) === String(p.product_id)) || null}
                    onChange={(_, newValue) =>
                      handleProductChange(index, 'product_id', newValue ? String(newValue.id) : '')
                    }
                    getOptionLabel={getProductLabel}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={filterProducts}
                    renderOption={renderSearchOption(
                      (option) => option.name,
                      (option) => {
                        const details = [
                          option.product_code,
                          option.category,
                          `Current in hand: ${option.current_quantity ?? 0}`,
                        ].filter(Boolean);
                        return details.join(' · ');
                      }
                    )}
                    renderInput={renderSearchInput('Search product...')}
                    fullWidth
                    size="small"
                    sx={autocompleteSx}
                  />
                </Box>
                <Box sx={{ width: { xs: 88, sm: 100 }, flexShrink: 0 }}>
                  <Typography sx={labelSx}>Qty *</Typography>
                  <TextField
                    type="number"
                    min="1"
                    value={p.quantity}
                    onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                    required
                    fullWidth
                    size="small"
                    placeholder="Qty"
                    sx={inputFieldSx}
                  />
                </Box>
                <Box sx={{ flexShrink: 0, pt: 2.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => removeProductRow(index)}
                    disabled={formData.products.length === 1}
                    sx={{
                      color: 'text.secondary',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                      '&.Mui-disabled': { opacity: 0.5 },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={addProductRow}
            sx={{ mb: 2.5, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Add Product
          </Button>

          {/* Pricing */}
          <Typography sx={sectionTitleSx}>Pricing</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Typography sx={labelSx}>Discount (₹)</Typography>
              <TextField
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                name="discount"
                value={formData.discount ?? ''}
                onChange={handleGeneralChange}
                fullWidth
                size="small"
                placeholder="0"
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={labelSx}>Advance Payment (₹)</Typography>
              <TextField
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                name="advance_payment"
                value={formData.advance_payment ?? ''}
                onChange={handleGeneralChange}
                fullWidth
                size="small"
                placeholder="0"
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={labelSx}>Transportation Charges (₹)</Typography>
              <TextField
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                name="transportation_charges"
                value={formData.transportation_charges ?? ''}
                onChange={handleGeneralChange}
                fullWidth
                size="small"
                placeholder="0"
                sx={inputFieldSx}
              />
            </Grid>
          </Grid>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Total Amount</Typography>
              <Typography variant="body2">₹{Number(totalAmount).toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Transportation Charges</Typography>
              <Typography variant="body2">₹{transportNum.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Discount</Typography>
              <Typography variant="body2">₹{discountNum.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2" fontWeight={600} color="text.primary">Final Price</Typography>
              <Typography variant="body2" fontWeight={600}>₹{finalPrice.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Advance Payment</Typography>
              <Typography variant="body2">₹{advanceNum.toFixed(2)}</Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading} sx={{ textTransform: 'none', fontWeight: 500 }}>
          Cancel
        </Button>
        <Button type="submit" form="edit-booking-form" variant="contained" color="primary" disabled={loading} sx={{ textTransform: 'none', fontWeight: 500 }}>
          {loading ? 'Updating...' : 'Update Booking'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingEditModal;
